import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import SelectInput from "../../components/forms/SelectInput";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";
import {
  TableShell,
  Table,
  Th,
  Td,
} from "../../components/data/Table";

export default function AdminManageServiceProgram() {
  const { t } = useTranslation();

  const [routes, setRoutes] = useState([]);
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [routesRes, busesRes, driversRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/admin/routes`),
        axios.get(`${process.env.REACT_APP_API_URL}/admin/buses`),
        axios.get(`${process.env.REACT_APP_API_URL}/admin/drivers`),
      ]);

      setRoutes(Array.isArray(routesRes.data) ? routesRes.data : []);
      setBuses(Array.isArray(busesRes.data) ? busesRes.data : []);
      setDrivers(Array.isArray(driversRes.data) ? driversRes.data : []);
    } catch (err) {
      console.error("Failed to fetch service program data", err);
      setMessage(t("admin_manage_service_program.messages.fetch_failed"));
      setMessageVariant("error");
    }
  };

  const busesById = useMemo(() => {
    const map = new Map();
    buses.forEach((bus) => map.set(String(bus.bus_id), bus));
    return map;
  }, [buses]);

  const driversById = useMemo(() => {
    const map = new Map();
    drivers.forEach((driver) => map.set(String(driver.driver_id), driver));
    return map;
  }, [drivers]);

  const updateRouteField = (routeId, field, value) => {
    setRoutes((prev) =>
      prev.map((route) =>
        route.route_id === routeId
          ? {
              ...route,
              [field]: value,
            }
          : route
      )
    );
  };

  const handleUpdateServiceProgram = async () => {
    try {
      const payload = {
        routes: routes.map((route) => ({
          route_id: Number(route.route_id),
          bus_id: route.bus_id ? Number(route.bus_id) : null,
          driver_id: route.driver_id ? Number(route.driver_id) : null,
          notes: route.notes || "",
        })),
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/service-program/update`,
        payload
      );

      setMessage(t("admin_manage_service_program.messages.success"));
      setMessageVariant("success");

      await fetchData();
    } catch (err) {
      console.error("Failed to update service program", err);
      setMessage(t("admin_manage_service_program.messages.failure"));
      setMessageVariant("error");
    }
  };

  return (
    <StandardScreen
      background="default"
      topNav={<TopNav title={t("admin_manage_service_program.title")} mode="main" />}
    >
      <ScreenContent>
        <Stack gap="4">
          <Card title={t("admin_manage_service_program.title")}>
            <Stack gap="4">
              <TableShell maxHeight="70vh">
                <Table>
                  <thead>
                    <tr>
                      <Th>{t("admin_manage_service_program.table.route_id")}</Th>
                      <Th>{t("admin_manage_service_program.table.route_name")}</Th>
                      <Th>{t("admin_manage_service_program.table.start_point")}</Th>
                      <Th>{t("admin_manage_service_program.table.end_point")}</Th>
                      <Th>{t("admin_manage_service_program.table.vendor_id")}</Th>
                      <Th>{t("admin_manage_service_program.table.bus_id")}</Th>
                      <Th>{t("admin_manage_service_program.table.driver_id")}</Th>
                      <Th>{t("admin_manage_service_program.table.notes")}</Th>
                    </tr>
                  </thead>

                  <tbody>
                    {routes.map((route) => (
                      <tr key={route.route_id}>
                        <Td>{route.route_id}</Td>
                        <Td>{route.route_name}</Td>
                        <Td>{route.start_point}</Td>
                        <Td>{route.end_point}</Td>
                        <Td>{route.vendor_id}</Td>

                        <Td>
                          <SelectInput
                            value={route.bus_id || ""}
                            onChange={(e) =>
                              updateRouteField(
                                route.route_id,
                                "bus_id",
                                e.target.value
                              )
                            }
                          >
                            <option value="">
                              {t("admin_manage_service_program.placeholders.no_bus")}
                            </option>

                            {buses.map((bus) => (
                              <option key={bus.bus_id} value={bus.bus_id}>
                                {[
                                  bus.bus_id,
                                  bus.bus_code,
                                  bus.plate_number,
                                ]
                                  .filter(Boolean)
                                  .join(" | ")}
                              </option>
                            ))}
                          </SelectInput>
                        </Td>

                        <Td>
                          <SelectInput
                            value={route.driver_id || ""}
                            onChange={(e) =>
                              updateRouteField(
                                route.route_id,
                                "driver_id",
                                e.target.value
                              )
                            }
                          >
                            <option value="">
                              {t("admin_manage_service_program.placeholders.no_driver")}
                            </option>

                            {drivers.map((driver) => (
                              <option key={driver.driver_id} value={driver.driver_id}>
                                {[
                                  driver.driver_id,
                                  driver.fullname,
                                  driver.phone,
                                ]
                                  .filter(Boolean)
                                  .join(" | ")}
                              </option>
                            ))}
                          </SelectInput>
                        </Td>

                        <Td>
                          <TextInput
                            value={route.notes || ""}
                            onChange={(e) =>
                              updateRouteField(
                                route.route_id,
                                "notes",
                                e.target.value
                              )
                            }
                          />
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableShell>

              <Button onClick={handleUpdateServiceProgram} fullWidth>
                {t("admin_manage_service_program.actions.update_service_program")}
              </Button>
            </Stack>
          </Card>

          <StatusMessage message={message} variant={messageVariant} />
        </Stack>
      </ScreenContent>
    </StandardScreen>
  );
}